import { useMemo } from 'react';

import { keepPreviousData, useQueries, useQuery } from '@tanstack/react-query';

import type {
  Bdkv2BookDescriptor,
  V2BookConnectionDescriptor,
} from '@/generated/hooks/bdk/models';
import {
  getRetrieveWorkspaceBookQueryKey,
  retrieveWorkspaceBook,
  retrieveWorkspaceBookConnectionsByIds,
} from '@/generated/hooks/bdk/v2/services/library';
import { useWorkspaceListToolSources } from '@/generated/hooks/rain/v1/workspace_scoped/tool_catalog_service';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores';

/**
 * Stable key for a book across the Connect step. An agent connects to a book
 * regardless of its version, so identity is the book name alone: the same book
 * under two versions is treated as one.
 */
export const bookKey = (book: { name?: string }): string => book.name ?? '';

/** A book the agent is connected to, with the connection and its tool source. */
export interface IConnectedBook {
  book: Bdkv2BookDescriptor;
  connection: V2BookConnectionDescriptor;
  /** tool source id backing this connection (for removal). */
  sourceId: string;
}

export interface IAgentConnectedBooks {
  /** Books the agent is connected to, in tool-source order. */
  connectedBooks: IConnectedBook[];
  /**
   * connection_id → tool source id, straight from the agent's tool sources.
   * Updates as soon as a source is added or removed, before the connection
   * descriptors for it have been fetched.
   */
  sourceByConnectionId: ReadonlyMap<string, string>;
  /** The connection descriptors batch-fetched for the agent's tool sources. */
  attachedConnections: V2BookConnectionDescriptor[];
  /** bookKey → { connection, sourceId } for quick per-card lookup. */
  connectedByBook: Map<
    string,
    { connection: V2BookConnectionDescriptor; sourceId: string }
  >;
  isLoading: boolean;
}

/**
 * Resolves the books an agent is connected to, independent of the paginated
 * `books:latest` catalog. Walks the config thread's tool sources for their
 * `connection_id`s, batch-gets those connections (which carry `book_name` /
 * `book_version`), then fetches each referenced book descriptor so the card can
 * render its icon and display name even when the book isn't on the catalog's
 * first page.
 *
 * Chain (each step depends on the previous): list tool sources → batch-get
 * connections by id → retrieve each unique book.
 */
export function useAgentConnectedBooks(
  agentId: string | null,
  threadId: string | null,
): IAgentConnectedBooks {
  const organization = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspace = useUserPreferencesStore(selectCurrentWorkspaceId);

  const scopeReady = Boolean(organization && workspace && agentId && threadId);

  // 1) Tool sources on the config thread → the connection_ids the agent uses.
  const { data: toolSourcesData, isLoading: isLoadingToolSources } =
    useWorkspaceListToolSources(
      organization,
      workspace,
      agentId ?? '',
      threadId ?? '',
      undefined,
      { query: { enabled: scopeReady } },
    );

  // connection_id → source_id, for the tool sources that carry a connection.
  const sourceByConnectionId = useMemo(() => {
    const map = new Map<string, string>();
    for (const source of toolSourcesData?.data.tool_sources ?? []) {
      const connectionId = source.config?.connection_id;
      if (typeof connectionId === 'string' && source.source_id) {
        map.set(connectionId, source.source_id);
      }
    }
    return map;
  }, [toolSourcesData]);

  // Sorted so the query key is stable regardless of tool-source order.
  const connectionIds = useMemo(
    () => [...sourceByConnectionId.keys()].sort(),
    [sourceByConnectionId],
  );

  // 2) Batch-get those connections (POST /connections:batchGet). Modeled as a
  // query since it's a pure read keyed by the connection_ids.
  const { data: connectionsData, isLoading: isLoadingConnections } = useQuery({
    queryKey: [
      'agent-connected-connections',
      organization,
      workspace,
      connectionIds,
    ],
    queryFn: () =>
      retrieveWorkspaceBookConnectionsByIds(organization, workspace, {
        connection_ids: connectionIds,
      }),
    enabled: Boolean(organization && workspace) && connectionIds.length > 0,
    // Adding or removing a connection changes the key; without this the
    // connections drop to a spinner until the new batch loads.
    placeholderData: keepPreviousData,
  });

  // Filtered by the live tool sources: `keepPreviousData` keeps a removed
  // connection in the previous batch until the next one lands, and forever
  // once the last one is removed and the query is disabled.
  const connections = useMemo(
    () =>
      (connectionsData?.data.book_connections ?? []).filter(connection =>
        sourceByConnectionId.has(connection.connection_id ?? ''),
      ),
    [connectionsData, sourceByConnectionId],
  );

  // Unique books referenced by those connections, keyed by name (a book is one
  // book regardless of version). On a name collision we keep the first version
  // seen and fetch that descriptor for display.
  const bookRefs = useMemo(() => {
    const seen = new Set<string>();
    const refs: { name: string; version: string }[] = [];
    for (const connection of connections) {
      if (!connection.book_name || !connection.book_version) continue;
      const key = bookKey({ name: connection.book_name });
      if (seen.has(key)) continue;
      seen.add(key);
      refs.push({
        name: connection.book_name,
        version: connection.book_version,
      });
    }
    return refs;
  }, [connections]);

  // 3) Fetch each referenced book descriptor (icon, display_name, …).
  const bookQueries = useQueries({
    queries: bookRefs.map(ref => ({
      queryKey: getRetrieveWorkspaceBookQueryKey(
        organization,
        workspace,
        ref.name,
        ref.version,
      ),
      queryFn: () =>
        retrieveWorkspaceBook(organization, workspace, ref.name, ref.version),
      enabled: Boolean(organization && workspace),
    })),
  });

  const isLoadingBooks = bookQueries.some(query => query.isLoading);

  const booksByKey = useMemo(() => {
    const map = new Map<string, Bdkv2BookDescriptor>();
    for (const query of bookQueries) {
      const book = query.data?.data.book;
      if (book) map.set(bookKey(book), book);
    }
    return map;
  }, [bookQueries]);

  // Assemble connected books in tool-source order. A connection whose book
  // descriptor didn't resolve keeps a bare name/version book so it still shows.
  const connectedBooks = useMemo(() => {
    const result: IConnectedBook[] = [];
    for (const connection of connections) {
      if (
        !connection.connection_id ||
        !connection.book_name ||
        !connection.book_version
      ) {
        continue;
      }
      const sourceId = sourceByConnectionId.get(connection.connection_id);
      if (!sourceId) continue;
      const book = booksByKey.get(bookKey({ name: connection.book_name })) ?? {
        name: connection.book_name,
        version: connection.book_version,
      };
      result.push({ book, connection, sourceId });
    }
    return result;
  }, [connections, sourceByConnectionId, booksByKey]);

  const connectedByBook = useMemo(() => {
    const map = new Map<
      string,
      { connection: V2BookConnectionDescriptor; sourceId: string }
    >();
    for (const entry of connectedBooks) {
      map.set(bookKey(entry.book), {
        connection: entry.connection,
        sourceId: entry.sourceId,
      });
    }
    return map;
  }, [connectedBooks]);

  // Only the steps that actually have work to do count toward loading, so an
  // agent with no connections isn't stuck "loading" forever.
  const isLoading =
    (scopeReady && isLoadingToolSources) ||
    (connectionIds.length > 0 && isLoadingConnections) ||
    (bookRefs.length > 0 && isLoadingBooks);

  return {
    connectedBooks,
    connectedByBook,
    sourceByConnectionId,
    attachedConnections: connections,
    isLoading,
  };
}
